
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CalendarDays, Clock, MapPin, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Visit {
  id: string;
  listing_id: string;
  student_id: string;
  owner_id: string;
  slot_start: string;
  slot_end: string;
  status: 'requested' | 'confirmed' | 'cancelled' | 'completed';
  notes?: string;
  listing_title?: string;
  student_name?: string;
}

interface VisitSchedulingProps {
  listingId?: string;
  listingTitle?: string;
  ownerId?: string;
}

const VisitScheduling: React.FC<VisitSchedulingProps> = ({ listingId, listingTitle, ownerId }) => {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (user) {
      fetchVisits();
    }
  }, [user]);

  const fetchVisits = async () => {
    try {
      const query = supabase
        .from('visits')
        .select(`
          id,
          listing_id,
          student_id,
          owner_id,
          slot_start,
          slot_end,
          status,
          notes,
          listings(title)
        `);

      if (profile?.role === 'student') {
        query.eq('student_id', user?.id);
      } else if (profile?.role === 'landlord') {
        query.eq('owner_id', user?.id);
      }

      const { data, error } = await query.order('slot_start', { ascending: true });

      if (error) throw error;

      const formattedVisits: Visit[] = (data || []).map(visit => ({
        id: visit.id,
        listing_id: visit.listing_id,
        student_id: visit.student_id,
        owner_id: visit.owner_id,
        slot_start: visit.slot_start,
        slot_end: visit.slot_end,
        status: visit.status as 'requested' | 'confirmed' | 'cancelled' | 'completed',
        notes: visit.notes,
        listing_title: (visit.listings as any)?.title || 'Unknown Property'
      }));

      setVisits(formattedVisits);
    } catch (error) {
      console.error('Error fetching visits:', error);
    }
  };

  const scheduleVisit = async () => {
    if (!selectedDate || !selectedTime || !listingId || !ownerId) {
      toast.error(t('fill_all_fields', 'يرجى ملء جميع الحقول', 'Please fill all required fields'));
      return;
    }

    setLoading(true);
    try {
      const [hours, minutes] = selectedTime.split(':');
      const startTime = new Date(selectedDate);
      startTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      const endTime = new Date(startTime);
      endTime.setHours(startTime.getHours() + 1); // 1 hour slot

      const { error } = await supabase
        .from('visits')
        .insert({
          listing_id: listingId,
          student_id: user?.id,
          owner_id: ownerId,
          slot_start: startTime.toISOString(),
          slot_end: endTime.toISOString(),
          notes: notes,
          status: 'requested'
        });

      if (error) throw error;

      toast.success(t('visit_requested', 'تم طلب الزيارة بنجاح', 'Visit requested successfully'));
      setDialogOpen(false);
      setSelectedDate(undefined);
      setSelectedTime('');
      setNotes('');
      fetchVisits();
    } catch (error) {
      console.error('Error scheduling visit:', error);
      toast.error(t('error_scheduling', 'خطأ في جدولة الزيارة', 'Error scheduling visit'));
    } finally {
      setLoading(false);
    }
  };

  const updateVisitStatus = async (visitId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('visits')
        .update({ status })
        .eq('id', visitId);

      if (error) throw error;

      toast.success(t('visit_updated', 'تم تحديث الزيارة', 'Visit updated'));
      fetchVisits();
    } catch (error) {
      console.error('Error updating visit:', error);
      toast.error(t('error_updating', 'خطأ في التحديث', 'Error updating'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500';
      case 'requested': return 'bg-yellow-500';
      case 'cancelled': return 'bg-red-500';
      case 'completed': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return t('confirmed', 'مؤكد', 'Confirmed');
      case 'requested': return t('requested', 'مطلوب', 'Requested');
      case 'cancelled': return t('cancelled', 'ملغي', 'Cancelled');
      case 'completed': return t('completed', 'مكتمل', 'Completed');
      default: return status;
    }
  };

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  return (
    <div className="space-y-6">
      {listingId && profile?.role === 'student' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              {t('schedule_visit', 'جدولة زيارة', 'Schedule a Visit')}
            </CardTitle>
            <CardDescription>
              {t('schedule_visit_desc', 'اطلب زيارة لمعاينة العقار', 'Request a visit to view this property')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full">
                  <CalendarDays className="h-4 w-4 mr-2" />
                  {t('request_visit', 'طلب زيارة', 'Request Visit')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('schedule_visit', 'جدولة زيارة', 'Schedule Visit')}</DialogTitle>
                  <DialogDescription>
                    {listingTitle && `${t('property', 'العقار', 'Property')}: ${listingTitle}`}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t('select_date', 'اختر التاريخ', 'Select Date')}
                    </label>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date() || date.getDay() === 5} // Disable past dates and Fridays
                      className="rounded-md border"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t('select_time', 'اختر الوقت', 'Select Time')}
                    </label>
                    <Select value={selectedTime} onValueChange={setSelectedTime}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('choose_time', 'اختر الوقت', 'Choose time')} />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t('notes', 'ملاحظات', 'Notes')} ({t('optional', 'اختياري', 'Optional')})
                    </label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={t('add_notes', 'أضف ملاحظات...', 'Add notes...')}
                    />
                  </div>
                  <Button onClick={scheduleVisit} disabled={loading} className="w-full">
                    {loading ? t('requesting', 'جاري الطلب...', 'Requesting...') : t('request_visit', 'طلب زيارة', 'Request Visit')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {profile?.role === 'student' 
              ? t('my_visits', 'زياراتي', 'My Visits')
              : t('visit_requests', 'طلبات الزيارة', 'Visit Requests')
            }
          </CardTitle>
          <CardDescription>
            {profile?.role === 'student'
              ? t('your_scheduled_visits', 'زياراتك المجدولة', 'Your scheduled property visits')
              : t('manage_visit_requests', 'إدارة طلبات الزيارة', 'Manage incoming visit requests')
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {visits.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {t('no_visits', 'لا توجد زيارات', 'No visits scheduled')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {visits.map((visit) => (
                <div key={visit.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-center justify-center p-2 bg-muted rounded">
                      <span className="text-sm font-medium">
                        {new Date(visit.slot_start).toLocaleDateString('en-US', { day: 'numeric' })}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(visit.slot_start).toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium">{visit.listing_title}</h4>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(visit.slot_start).toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                        {profile?.role === 'landlord' && visit.student_name && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {visit.student_name}
                          </span>
                        )}
                      </div>
                      {visit.notes && (
                        <p className="text-sm text-muted-foreground mt-1">{visit.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(visit.status)}>
                      {getStatusText(visit.status)}
                    </Badge>
                    {profile?.role === 'landlord' && visit.status === 'requested' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => updateVisitStatus(visit.id, 'confirmed')}
                        >
                          {t('confirm', 'تأكيد', 'Confirm')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateVisitStatus(visit.id, 'cancelled')}
                        >
                          {t('decline', 'رفض', 'Decline')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VisitScheduling;
